import React, { useState } from 'react';
import { Flex, Input, Table, Button, Typography, Tooltip, message, ConfigProvider, Empty } from 'antd';
import { DesktopOutlined, MobileOutlined, SearchOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { requestCaching } from '../../actions/cachedPagesActions';
import { Link as RouteLink } from 'react-router-dom';
import { usePrerenderUser } from '../../hooks/usePrerenderUser';
import { PointInTime, POINT_IN_TIME_COLUMN_WIDTH } from '../../components/PointInTime';
import ExportButton from '../../components/ExportButton';
import SeoScoreTag from './components/SeoScoreTag';
import ButtonWithRole from '../../components/ButtonWithRole';
import USER_ROLES from '../../features/auth/userRoles';
import { useEvent } from '../events/hooks/useEvent';
import { SeoScoreModal } from '../seoScoreModal/SeoScoreModal';

const { Link } = Typography;

export const SeoReportUrlsTable = ({
  urls,
  isFetching,
  tableParams,
  handleTableChange,
  debouncedSetTableParams,
  totalUrls,
  exportModalOpen,
}) => {
  const [searchInput, setSearchInput] = useState('');
  const dispatch = useDispatch();
  const [messageApi, contextHolder] = message.useMessage();
  const [showSeoScoreModal, setShowSeoScoreModal] = useState(false);
  const [cachedUrl, setCachedUrl] = useState();
  const prerenderUser = usePrerenderUser();
  const { track } = useEvent();

  const onInputChange = (e) => {
    const newInput = e.target.value;
    setSearchInput(newInput);
    debouncedSetTableParams(newInput);
  };

  const handleRecache = (url) => {
    dispatch(
      requestCaching('all', [url], () => {
        messageApi.open({
          type: 'success',
          content: (
            <span>
              1 URL was added to the Priority Recache Queue. <RouteLink to="/recache-queue">View Queue</RouteLink>
            </span>
          ),
        });
      })
    );
  };

  const openSeoScoreModal = (url) => {
    if (url.seo_score !== null) {
      setCachedUrl(url);
      setShowSeoScoreModal(true);
      track('SEO Score Clicked', { subscription_plan: prerenderUser.chargebeePlanId, location: 'SEO report' });
    }
  };

  return (
    <>
      {contextHolder}
      <SeoScoreModal open={showSeoScoreModal} onClose={() => setShowSeoScoreModal(false)} url={cachedUrl} />
      <Flex justify="space-between">
        <Input
          placeholder="search url"
          allowClear
          value={searchInput}
          onChange={onInputChange}
          prefix={<SearchOutlined style={{ color: '#D9D9D9' }} />}
          style={{ marginBottom: 16, width: '30%' }}
        />
        <ExportButton onClick={exportModalOpen} />
      </Flex>
      <ConfigProvider renderEmpty={() => <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No URLs found" />}>
        <Table
          loading={isFetching}
          dataSource={urls}
          onChange={handleTableChange}
          bordered
          pagination={{
            current: tableParams.pagination.current,
            pageSize: tableParams.pagination.pageSize,
            total: totalUrls,
          }}
          rowKey="id"
        >
          <Table.Column
            title="URL"
            dataIndex="url"
            key="url"
            ellipsis
            render={(url) => (
              <Link href={url} target="_blank" rel="noreferrer" title={url}>
                {url}
              </Link>
            )}
          />
          <Table.Column
            title="Device"
            dataIndex="adaptive_type"
            key="adaptive_type"
            align="center"
            width={100}
            filters={[
              {
                text: 'Desktop',
                value: 'desktop',
              },
              {
                text: 'Mobile',
                value: 'mobile',
              },
            ]}
            render={(adaptiveType) => (
              <Tooltip
                title={`Rendering was optimized for ${adaptiveType === 'mobile' ? 'Mobile' : 'Desktop'} crawlers`}
                placement="top"
              >
                {adaptiveType === 'mobile' ? <MobileOutlined /> : <DesktopOutlined />}
              </Tooltip>
            )}
          />
          <Table.Column
            title="SEO score"
            dataIndex="seo_score"
            key="seo_score"
            align="center"
            width={120}
            sorter
            render={(score, record) => (
              <Button
                type="ghost"
                size="small"
                onClick={() => {
                  openSeoScoreModal(record);
                }}
              >
                <SeoScoreTag score={score} />
              </Button>
            )}
          />

          <Table.Column
            title="Last Cached"
            dataIndex="last_refresh"
            key="last_refresh"
            width={POINT_IN_TIME_COLUMN_WIDTH}
            align="center"
            sorter
            render={(lastRefresh) => <PointInTime value={lastRefresh} />}
          />
          <Table.Column
            title=""
            key="recache"
            width={120}
            align="center"
            render={(_, record) => (
              <ButtonWithRole
                disabledFor={[USER_ROLES.BILLING_MANAGER, USER_ROLES.GUEST]}
                size="small"
                onClick={() => {
                  handleRecache(record);
                }}
              >
                Recache
              </ButtonWithRole>
            )}
          />
        </Table>
      </ConfigProvider>
    </>
  );
};
